import { Link } from "react-router-dom";
import {
  Zap, Headset, ShieldCheck, TrendingDown, Truck, Boxes, Thermometer, Container,
  Warehouse, Anchor, TrainIcon, ArrowUpRight, MapPin, Users, Clock3, BadgeCheck, Quote as QuoteIcon,
} from "lucide-react";
import { KineticLines, Reveal, Marquee, SectionHead, PrimaryButton, GhostButton, DotList } from "@/components/bits";
import { HomeHero } from "@/components/HomeHero";
import { IMG, SERVICES, TESTIMONIALS, MARQUEE_ITEMS, MEMBERSHIPS } from "@/lib/data";
import { useArticles } from "@/lib/articles";

const ICONS = { anchor: Anchor, train: TrainIcon, truck: Truck, boxes: Boxes, thermometer: Thermometer, container: Container, warehouse: Warehouse, zap: Zap };
const WRAP = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";

/* ---------- Overlapping highlight cards (under hero) ---------- */

function Highlights() {
  const items = [
    { icon: MapPin, k: "Locations", v: "Ports of LA, Long Beach & Oakland — lanes across the Southwest" },
    { icon: Users, k: "Brokers", v: "Licensed brokers on the desk, supporting every load you tender" },
    { icon: Truck, k: "Carriers", v: "Vetted carrier network — authority, insurance and safety checked" },
    { icon: Clock3, k: "On Time", v: "Appointment discipline and live updates on every single move" },
  ];
  return (
    <section className={`${WRAP} relative pt-10 z-10`} data-testid="highlights-section">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {items.map((it, i) => (
          <Reveal key={it.k} delay={0.08 * i}>
            <div className="card card-hover p-7 h-full" data-testid={`highlight-card-${i}`}>
              <div className="h-11 w-11 rounded-2xl bg-signal/10 flex items-center justify-center">
                <it.icon className="h-5 w-5 text-corpblue" />
              </div>
              <p className="mt-5 font-display text-2xl font-bold text-ink">{it.k}</p>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{it.v}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------- Why Ferry ---------- */

function WhyFerry() {
  const pillars = [
    { icon: Zap, title: "Instant freight accommodation", body: "Same-day capacity when a load goes sideways — we quote and cover fast." },
    { icon: Headset, title: "24/7 support team", body: "A named dispatcher who answers at 2am, not a ticket queue." },
    { icon: ShieldCheck, title: "Damage-free focus", body: "Vetted carriers, verified insurance, cargo-insured loads." },
    { icon: TrendingDown, title: "Low, honest costs", body: "Market-rate pricing without the padding, fees quoted up front." },
  ];
  return (
    <section className="py-20 lg:py-28" data-testid="why-section">
      <div className={WRAP}>
        <Reveal>
          <div className="card p-6 sm:p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center" data-testid="why-panel">
            <div className="lg:col-span-5 relative">
              <div className="img-frame overflow-hidden rounded-3xl grain">
                <img src={IMG.cargoShip} alt="Container ship at port" loading="lazy" className="img-zoom w-full aspect-[4/3] object-cover" />
              </div>
              <div className="absolute -bottom-5 right-3 sm:right-6 panel-reference px-6 py-5 shadow-lift" data-testid="why-promise-card">
                <p className="font-display text-3xl font-bold">Zero</p>
                <p className="label text-[10px] text-brand-light mt-1">double-brokerage · in writing</p>
              </div>
            </div>
            <div className="lg:col-span-7">
              <SectionHead
                eyebrow="Why Ferry"
                title="Delivering more than just freight — experience the Ferry difference."
                lead="Our commitment to innovation means we keep streamlining how freight gets quoted, covered and tracked, while our trust in our team and partners means you can always count on us to deliver."
              />
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                <DotList items={["Instant freight accommodation", "24/7 support team", "Professional & reliable carrier network"]} />
                <DotList items={["Services for all industries", "Damage-free load delivery", "Low, honest shipping costs"]} />
              </div>
            </div>
          </div>
        </Reveal>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {pillars.map((p, i) => (
            <Reveal key={p.title} delay={0.06 * i}>
              <div className="card card-hover h-full p-7" data-testid={`why-pillar-${i}`}>
                <div className="h-11 w-11 rounded-2xl bg-signal/10 flex items-center justify-center">
                  <p.icon className="h-5 w-5 text-corpblue" />
                </div>
                <h3 className="mt-5 font-display text-lg font-bold text-ink">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Services grid ---------- */

export function ServiceCard({ s, i }) {
  const Icon = ICONS[s.icon] || Truck;
  return (
    <Reveal delay={0.05 * (i % 4)} className="h-full">
      <Link
        to={`/services/${s.slug}`}
        data-testid={`service-card-${s.slug}`}
        className="group card card-hover flex flex-col h-full"
      >
        <div className="relative aspect-[16/11] overflow-hidden">
          <img src={s.image} alt={s.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute top-4 left-4 h-10 w-10 rounded-xl bg-white/90 backdrop-blur flex items-center justify-center shadow-soft">
            <Icon className="h-[18px] w-[18px] text-corpblue" />
          </div>
        </div>
        <div className="p-6 flex flex-col flex-1">
          <p className="label text-[10px] text-signal">Service</p>
          <h3 className="mt-2 font-display text-lg font-bold text-ink group-hover:text-corpblue transition-colors">{s.name}</h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{s.short}</p>
          <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-corpblue">
            Learn more <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </div>
      </Link>
    </Reveal>
  );
}

function ServicesHub() {
  return (
    <section className="pb-20 lg:pb-28" data-testid="services-hub">
      <div className={WRAP}>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <SectionHead
            eyebrow="Services"
            title="Whether it is LTL, FTL, containers or intermodal drayage — we handle every shipping type."
            lead="One brokerage for the whole freight mix, so you never juggle vendors per mode again."
          />
          <Reveal delay={0.2}>
            <GhostButton to="/services" testid="services-hub-all-link">All services</GhostButton>
          </Reveal>
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s, i) => <ServiceCard key={s.slug} s={s} i={i} />)}
        </div>
      </div>
    </section>
  );
}

/* ---------- Technology ---------- */

function Technology() {
  return (
    <section className="pb-20 lg:pb-28" data-testid="technology-section">
      <div className={WRAP}>
        <Reveal>
          <div className="panel-reference p-6 sm:p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center" data-testid="technology-panel">
            <div>
              <SectionHead
                light
                eyebrow="Technology"
                title="Better shipment visibility and stronger communication across every move."
                lead="Dispatch visibility, operational communication and practical tools — so you always know where your freight stands, from pickup to proof of delivery."
              />
              <div className="mt-8">
                <DotList
                  light
                  testid="technology-bullets"
                  items={[
                    "Dispatch and shipment tracking support",
                    "Operational updates for active loads",
                    "Practical tools for partner coordination",
                    "Visibility that helps reduce uncertainty",
                  ]}
                />
              </div>
              <Reveal delay={0.3}>
                <div className="mt-9 flex flex-wrap gap-4">
                  <PrimaryButton to="/track" testid="technology-track-cta">Track a shipment</PrimaryButton>
                  <GhostButton to="/quote" light testid="technology-quote-cta">Put us on a lane</GhostButton>
                </div>
              </Reveal>
            </div>
            <div className="relative">
              <div className="img-frame overflow-hidden rounded-3xl grain border border-white/10">
                <img src={IMG.techAerial} alt="Aerial view of a container terminal" loading="lazy" className="img-zoom w-full aspect-[4/3] object-cover" />
              </div>
              <div className="absolute -bottom-5 left-6 card-sm px-6 py-5 shadow-lift" data-testid="technology-stat-card">
                <p className="font-display text-2xl font-bold text-ink">Every load</p>
                <p className="label text-[10px] text-slate-500 mt-1">Tracked · Updated · Confirmed</p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Warehousing (white panel) ---------- */

function Warehousing() {
  const s = SERVICES.find((x) => x.slug === "warehousing");
  return (
    <section className="pb-20 lg:pb-28" data-testid="warehousing-section">
      <div className={WRAP}>
        <Reveal>
          <div className="card p-6 sm:p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="img-frame overflow-hidden rounded-3xl grain order-last lg:order-first">
              <img src={s.image} alt="Distribution warehouse with pallet racking" loading="lazy" className="img-zoom w-full aspect-[4/3] object-cover" />
            </div>
            <div>
              <SectionHead
                eyebrow="Warehousing & Distribution"
                title="Seamless warehousing services."
                lead="One-stop support for distribution, warehousing and transportation — for shippers of every size. We take the box, de-van it, pallet it and stage it. One price, one team."
              />
              <div className="mt-8">
                <DotList items={s.bullets} testid="warehousing-bullets" />
              </div>
              <Reveal delay={0.3}>
                <div className="mt-9">
                  <GhostButton to="/services/warehousing" testid="warehousing-detail-cta">Explore warehousing</GhostButton>
                </div>
              </Reveal>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Memberships / affiliations ---------- */

export function Memberships() {
  return (
    <section className="pb-20 lg:pb-28" data-testid="memberships-section">
      <div className={WRAP}>
        <Reveal>
          <div className="card p-6 sm:p-10 lg:p-14">
            <SectionHead
              eyebrow="Our Memberships"
              title="Ferry Freight: moving your supply chain forward with excellence."
              lead="Trusted industry affiliations and licensing that reinforce our commitment to professional freight brokerage standards."
            />
            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {MEMBERSHIPS.map((m, i) => (
                <Reveal key={m.abbr} delay={0.05 * i} className="h-full">
                  <div className="group h-full rounded-2xl border border-slate-100 bg-paper px-4 py-6 text-center transition-colors hover:border-signal/40 hover:bg-white" data-testid={`membership-badge-${i}`} title={m.note}>
                    <div className="mx-auto h-10 w-10 rounded-full bg-white shadow-soft flex items-center justify-center">
                      <BadgeCheck className="h-5 w-5 text-corpblue" />
                    </div>
                    <p className="mt-4 font-display text-lg font-bold text-ink tracking-tight">{m.abbr}</p>
                    <p className="mt-0.5 text-xs font-semibold text-slate-500">{m.name}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Testimonials ---------- */

function Testimonials() {
  return (
    <section className="pb-20 lg:pb-28" data-testid="testimonials-section">
      <div className={WRAP}>
        <SectionHead
          eyebrow="Testimonials"
          title="Trusted by customers who value communication and dependable delivery."
          lead="What shippers and carriers say about working with the Ferry Freight dispatch desk."
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={0.08 * i} className="h-full">
              <figure className="card card-hover h-full p-8 flex flex-col" data-testid={`testimonial-card-${i}`}>
                <div className="h-10 w-10 rounded-full bg-signal/10 flex items-center justify-center">
                  <QuoteIcon className="h-4 w-4 text-corpblue" />
                </div>
                <blockquote className="mt-5 text-ink leading-relaxed flex-1">"{t.quote}"</blockquote>
                <figcaption className="mt-6 pt-5 border-t border-slate-100">
                  <p className="font-display font-bold text-ink">{t.name}</p>
                  <p className="text-sm text-slate-500 mt-0.5">{t.role}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Insights preview ---------- */

function InsightsPreview() {
  const { articles } = useArticles(3);
  return (
    <section className="pb-20 lg:pb-28" data-testid="insights-preview">
      <div className={WRAP}>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <SectionHead eyebrow="Latest Insights" title="Industry topics that support smarter shipping decisions." />
          <Reveal delay={0.2}>
            <GhostButton to="/insights" testid="insights-preview-all-link">All insights</GhostButton>
          </Reveal>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {articles.slice(0, 3).map((p, i) => (
            <Reveal key={p.slug} delay={0.07 * i} className="h-full">
              <Link to={`/insights/${p.slug}`} data-testid={`insight-card-${p.slug}`} className="group card card-hover flex flex-col h-full">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <p className="label text-[10px] text-signal">{p.category} · {p.date}</p>
                  <h3 className="mt-3 font-display text-lg font-bold text-ink leading-snug group-hover:text-corpblue transition-colors">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-2 flex-1">{p.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-corpblue">
                    Read more <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- CTA banner ---------- */

export function CTABanner() {
  return (
    <section className="pb-20 lg:pb-28" data-testid="cta-banner">
      <div className={WRAP}>
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl panel-reference" data-testid="cta-banner-panel">
            <div className="relative px-6 sm:px-10 lg:px-14 py-16 lg:py-20 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
              <div className="max-w-2xl">
                <KineticLines
                  lines={["Share your route.", "Get a freight quote."]}
                  testid="cta-banner-headline"
                  className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white"
                />
                <p className="mt-5 text-slate-300 text-lg leading-relaxed">
                  Tell us your pickup, destination and equipment needs. Our team will put together a quote for your shipment.
                </p>
              </div>
              <div className="flex flex-wrap gap-4">
                <PrimaryButton to="/quote" testid="cta-banner-quote-button">Request a Quote</PrimaryButton>
                <GhostButton to="/contact" light testid="cta-banner-contact-button">Contact Us</GhostButton>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Page ---------- */

export default function Home() {
  return (
    <main>
      <HomeHero />
      <Highlights />
      <WhyFerry />
      <div className="mb-20 lg:mb-28"><Marquee items={MARQUEE_ITEMS} /></div>
      <ServicesHub />
      <Technology />
      <Warehousing />
      <Memberships />
      <Testimonials />
      <InsightsPreview />
      <CTABanner />
    </main>
  );
}
